<template>
  <div class="container">
    <header class="header">
      <h1>診所預約提醒產生器</h1>
      <p class="subtitle">快速建立行事曆提醒，協助病患追蹤疫苗接種時程</p>
    </header>

    <!-- 表單區域 -->
    <div id="formSection" class="card" v-show="currentView === 'form'">
      <div class="card-header">
        <h2>建立預約提醒</h2>
      </div>
      <form @submit.prevent="handleSubmit" class="card-body">
        <div class="form-group">
          <label for="title">提醒事項*</label>
          <select
            id="title"
            v-model="eventData.title"
            @change="updateDateByDose"
            required>
            <option value="第一劑注射提醒">第一劑注射提醒</option>
            <option value="第二劑注射提醒">第二劑注射提醒</option>
            <option value="第三劑注射提醒">第三劑注射提醒</option>
          </select>
        </div>
        <div class="form-row">
          <div class="form-group flex-1">
            <label for="startDate">預約時間*</label>
            <input
              type="datetime-local"
              id="startDate"
              v-model="eventData.startDate"
              @change="updateEndDate"
              required />
          </div>
          <div class="form-group flex-1">
            <label for="endDate">結束時間*</label>
            <input
              type="datetime-local"
              id="endDate"
              v-model="eventData.endDate"
              required />
          </div>
        </div>
        <div class="form-group">
          <label for="location">診所名稱</label>
          <input type="text" id="location" v-model="eventData.location" />
        </div>
        <div class="form-group">
          <label for="description">備註說明</label>
          <textarea id="description" v-model="eventData.description"></textarea>
        </div>
        <div class="form-actions">
          <button type="submit" class="btn btn-primary">
            產生預約 QR Code
          </button>
        </div>
      </form>
    </div>

    <!-- QR Code 顯示區域 -->
    <div id="qrcodeSection" class="card" v-show="currentView === 'qrcode'">
      <div class="card-header">
        <h2>預約 QR Code</h2>
      </div>
      <div class="card-body text-center">
        <div class="qrcode-container">
          <div id="qrcode" ref="qrcodeDiv"></div>
        </div>
        <p class="instructions">請掃描 QR Code 加入行事曆提醒</p>
        <div class="button-group">
          <button
            @click="showEventDetails"
            type="button"
            class="btn btn-secondary">
            預覽提醒內容
          </button>
          <button @click="backToForm" class="btn btn-outline">返回表單</button>
        </div>
      </div>
    </div>

    <!-- 活動詳情區域 -->
    <div id="eventDetails" class="card" v-show="currentView === 'details'">
      <div class="card-header">
        <h2>預約提醒詳情</h2>
      </div>
      <div class="card-body">
        <div class="event-info">
          <h3>{{ eventData.title }}</h3>
          <p>
            <strong>📅 預約時間：</strong
            >{{ formatDateTime(eventData.startDate) }}
          </p>
          <p>
            <strong>🏥 診所名稱：</strong>{{ eventData.location || '未提供' }}
          </p>
          <p v-if="eventData.description">
            <strong>📝 備註說明：</strong>{{ eventData.description }}
          </p>
        </div>
        <div class="button-group">
          <button @click="addToCalendar" class="btn btn-primary">
            加入行事曆
          </button>
          <button @click="addToGoogleCalendar" class="btn btn-secondary">
            加入 Google 行事曆
          </button>
        </div>
      </div>
    </div>
    <div v-if="isPreviewMode" class="button-group">
      <button @click="backToForm" type="button" class="btn btn-outline">
        返回表單
      </button>
      <button @click="backToQRCode" class="btn btn-outline">
        返回 QR Code
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, nextTick } from 'vue';

// 狀態管理
const currentView = ref('form');
const qrcodeDiv = ref(null);
const eventUrl = ref('');
const isPreviewMode = ref(false); // 標記是否為預覽模式

// 取得當前日期時間
function getCurrentDateTime() {
  const now = new Date();
  now.setMinutes(now.getMinutes() - (now.getMinutes() % 15));
  return now;
}

function getDateString(date) {
  return new Date(date - date.getTimezoneOffset() * 60000)
    .toISOString()
    .slice(0, 16);
}

// 事件資料
const eventData = ref({
  title: '第一劑注射提醒',
  location: '',
  description: '請攜帶健保卡與相關證件',
  startDate: getDateString(getCurrentDateTime()),
  endDate: '',
});

// 根據選擇的劑型更新日期
function updateDateByDose() {
  const title = eventData.value.title;
  let daysToAdd = 0;

  if (title === '第二劑注射提醒') {
    daysToAdd = 60; // 第二劑間隔60天
  } else if (title === '第三劑注射提醒') {
    daysToAdd = 120; // 第三劑間隔120天
  }
  const targetDate = getCurrentDateTime();
  if (daysToAdd > 0) {
    targetDate.setDate(targetDate.getDate() + daysToAdd);
  }
  eventData.value.startDate = getDateString(targetDate);
  // 更新結束時間
  updateEndDate();
}

// 當起始時間變更時更新結束時間
function updateEndDate() {
  if (eventData.value.startDate) {
    const startDate = new Date(eventData.value.startDate);
    const endDate = new Date(startDate.getTime() + 30 * 60000); // 加 30 分鐘

    // 格式化日期時間為 datetime-local 格式
    const endDateTime = new Date(
      endDate.getTime() - endDate.getTimezoneOffset() * 60000
    )
      .toISOString()
      .slice(0, 16);

    eventData.value.endDate = endDateTime;
  }
}

// 提交表單
function handleSubmit() {
  // 產生包含活動資訊的 URL
  eventUrl.value = generateEventUrl();

  // 產生 QR Code
  nextTick(() => {
    if (qrcodeDiv.value) {
      qrcodeDiv.value.innerHTML = '';
      new window.QRCode(qrcodeDiv.value, {
        text: eventUrl.value,
        width: 256,
        height: 256,
      });
      console.log(eventUrl.value);
    }
  });

  currentView.value = 'qrcode';
}

// 返回表單
function backToForm() {
  currentView.value = 'form';
  isPreviewMode.value = false;
}

// 顯示活動詳情
function showEventDetails() {
  isPreviewMode.value = true; // 標記為預覽模式
  currentView.value = 'details';
}

// 返回 QR Code 頁面
function backToQRCode() {
  currentView.value = 'qrcode';
  isPreviewMode.value = false;
}

// 獲取劑型代碼
function getDoseCode(title) {
  if (title.includes('第一劑')) return 'd1';
  if (title.includes('第二劑')) return 'd2';
  if (title.includes('第三劑')) return 'd3';
  return 'd1'; // 預設
}

// 產生活動 URL
function generateEventUrl() {
  // 獲取劑型代碼
  const doseCode = getDoseCode(eventData.value.title);

  // 將診所名稱編碼
  const encodedClinic = encodeURIComponent(eventData.value.location);

  // 為了支援舊版解析，保留 d 參數
  const shortData = {
    t: eventData.value.title,
    l: eventData.value.location,
    d: eventData.value.description,
    s: eventData.value.startDate,
    e: eventData.value.endDate,
  };
  const encodedData = encodeURIComponent(JSON.stringify(shortData));

  // 建立包含診所和劑型的URL
  const baseUrl = `${window.location.origin}/clinic/${encodedClinic}/${doseCode}`;
  return `${baseUrl}?d=${encodedData}`;
}

// 格式化日期時間
function formatDateTime(dateTimeStr) {
  if (!dateTimeStr) return '';
  const date = new Date(dateTimeStr);
  return date.toLocaleString('zh-TW', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

// 加入行事曆（使用 .ics 格式）
function addToCalendar() {
  const icsContent = generateICSContent();

  // 檢測是否為 iOS 設備
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

  if (isIOS) {
    // iOS 設備使用 data URI scheme
    window.location.href =
      'data:text/calendar;charset=utf-8,' + encodeURIComponent(icsContent);
  } else {
    // 其他設備下載檔案
    const blob = new Blob([icsContent], {
      type: 'text/calendar;charset=utf-8',
    });
    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    link.setAttribute('download', `${eventData.value.title}.ics`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
}

// 產生 ICS 檔案內容
function generateICSContent() {
  const formatICSDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toISOString().replace(/[-:.]/g, '').slice(0, -4) + 'Z';
  };

  const escapeText = (text) => {
    if (!text) return '';
    return text.replace(/[\\;,]/g, '\\$&').replace(/\n/g, '\\n');
  };

  return `BEGIN:VCALENDAR
VERSION:2.0
PRODID:-//診所預約提醒//NONSGML v1.0//TW
CALSCALE:GREGORIAN
BEGIN:VEVENT
SUMMARY:${escapeText(eventData.value.title)}
DTSTART:${formatICSDate(eventData.value.startDate)}
DTEND:${formatICSDate(eventData.value.endDate)}
${
  eventData.value.location
    ? `LOCATION:${escapeText(eventData.value.location)}`
    : ''
}
${
  eventData.value.description
    ? `DESCRIPTION:${escapeText(eventData.value.description)}`
    : ''
}
END:VEVENT
END:VCALENDAR`;
}

// 加入 Google 行事曆
function addToGoogleCalendar() {
  const baseUrl = 'https://calendar.google.com/calendar/render';
  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: eventData.value.title,
    dates: `${formatGoogleDate(eventData.value.startDate)}/${formatGoogleDate(
      eventData.value.endDate
    )}`,
    location: eventData.value.location || '',
    details: eventData.value.description || '',
  });

  window.open(`${baseUrl}?${params.toString()}`, '_blank');
}

// 格式化 Google 行事曆日期
function formatGoogleDate(dateStr) {
  const date = new Date(dateStr);
  return date.toISOString().replace(/[-:.]/g, '').slice(0, -4) + 'Z';
}

// 檢查 URL 參數並顯示對應內容
onMounted(() => {
  // 檢查URL中是否有d參數（已加密的詳情資料）
  const urlParams = new URLSearchParams(window.location.search);
  if (urlParams.has('d')) {
    try {
      const decodedData = JSON.parse(decodeURIComponent(urlParams.get('d')));
      // 轉換回原始資料格式
      eventData.value = {
        title: decodedData.t,
        location: decodedData.l,
        description: decodedData.d,
        startDate: decodedData.s,
        endDate: decodedData.e,
      };
      isPreviewMode.value = false; // 民眾掃描QR碼進入，非預覽模式
      currentView.value = 'details';
    } catch (error) {
      console.error('無法解析活動資料:', error);
      currentView.value = 'form';
    }
  } else {
    // 檢查URL路徑中是否包含診所資訊
    const pathParts = window.location.pathname.split('/');
    if (pathParts.length >= 3 && pathParts[1] === 'clinic') {
      // 從URL路徑讀取診所名稱
      const clinicName = decodeURIComponent(pathParts[2]);
      if (clinicName) {
        eventData.value.location = clinicName;
      }
    }

    currentView.value = 'form';
    // 確保有初始日期
    updateDateByDose();
  }
});
</script>

<style>
/* 全局樣式 */
:root {
  --primary-color: #2563eb;
  --secondary-color: #475569;
  --success-color: #059669;
  --background-color: #f1f5f9;
  --border-color: #e2e8f0;
  --text-color: #1e293b;
  --text-light: #64748b;
  --card-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1),
    0 2px 4px -2px rgb(0 0 0 / 0.1);
  --radius: 0.5rem;
}

body {
  background-color: var(--background-color);
  color: var(--text-color);
  line-height: 1.6;
  margin: 0;
  padding: 1rem;
}

.container {
  max-width: 800px;
  margin: 0 auto;
  padding: 1rem;
}

/* 頁首樣式 */
.header {
  text-align: center;
  margin-bottom: 2rem;
}

.header h1 {
  color: var(--primary-color);
  font-size: 1.875rem;
  margin-bottom: 0.5rem;
}

.subtitle {
  color: var(--text-light);
  margin: 0;
}

/* 卡片樣式 */
.card {
  background: white;
  border-radius: var(--radius);
  box-shadow: var(--card-shadow);
  margin-bottom: 1.5rem;
  overflow: hidden;
}

.card-header {
  background-color: #f8fafc;
  border-bottom: 1px solid var(--border-color);
  padding: 1rem 1.5rem;
}

.card-header h2 {
  color: var(--text-color);
  font-size: 1.25rem;
  margin: 0;
}

.card-body {
  padding: 1.5rem;
}

/* 表單樣式 */
.form-group {
  margin-bottom: 1.25rem;
}

.form-row {
  display: flex;
  gap: 1rem;
  margin-bottom: 1.25rem;
}

.flex-1 {
  flex: 1;
}

label {
  display: block;
  font-weight: 500;
  margin-bottom: 0.5rem;
  color: var(--text-color);
}

input[type='text'],
input[type='datetime-local'],
textarea,
select {
  width: 100%;
  padding: 0.625rem;
  border: 1px solid var(--border-color);
  border-radius: var(--radius);
  font-size: 0.875rem;
  transition: border-color 0.2s;
  box-sizing: border-box;
}

input:focus,
textarea:focus {
  outline: none;
  border-color: var(--primary-color);
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
}

textarea {
  height: 5rem;
  resize: vertical;
}

/* 按鈕樣式 */
.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.625rem 1.25rem;
  border-radius: var(--radius);
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
  font-size: 0.875rem;
}

.btn-primary {
  background-color: var(--primary-color);
  color: white;
}

.btn-primary:hover {
  background-color: #1d4ed8;
}

.btn-secondary {
  background-color: var(--secondary-color);
  color: white;
}

.btn-secondary:hover {
  background-color: #334155;
}

.btn-outline {
  background-color: white;
  border: 1px solid var(--border-color);
  color: var(--text-light);
}

.btn-outline:hover {
  background-color: #f8fafc;
}

/* QR Code 樣式 */
.qrcode-container {
  display: flex;
  justify-content: center;
  margin: 1.5rem 0;
  padding: 1rem;
  background-color: white;
  border-radius: var(--radius);
}

#qrcode {
  padding: 1rem;
  background: white;
  border-radius: var(--radius);
  box-shadow: 0 1px 3px rgb(0 0 0 / 0.1);
}

.instructions {
  color: var(--text-light);
  margin: 1rem 0;
}

.small-text {
  font-size: 0.75rem;
  color: var(--text-light);
}

/* 按鈕組 */
.button-group {
  display: flex;
  gap: 0.75rem;
  justify-content: center;
  margin-top: 1.5rem;
}

/* 活動詳情樣式 */
.event-info {
  background-color: #f8fafc;
  padding: 1rem;
  border-radius: var(--radius);
  margin-bottom: 1rem;
}

.event-info h3 {
  color: var(--primary-color);
  margin: 0 0 1rem 0;
}

.event-info p {
  margin: 0.5rem 0;
}

/* 響應式設計 */
@media (max-width: 640px) {
  .form-row {
    flex-direction: column;
    gap: 0;
  }

  .button-group {
    flex-direction: column;
  }

  .btn {
    width: 100%;
  }
}

.text-center {
  text-align: center;
}
</style>
